import React, { useEffect, useRef, useState } from "react";
import { i18n, useTranslation } from "../i18n";

import LoadingSpinner from "./LoadingSpinner";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearAuthSessionAction } from "../actions/auth";
import {
  getNotificationListAction,
  getUnreadNotificationCountAction,
  markNotificationReadAction,
  NOTIFICATION_TYPES,
} from "../actions/notification";
import { getVendorProfileView } from "../utils/vendorProfile";
import { RoutesPaths } from "../Router/constant";

const NOTIFICATIONS_PER_PAGE = 10;

const getInternalNotificationPath = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  const target = value.trim();
  if (!/^(https?:\/\/|\/|\.\/|\.\.\/)/i.test(target) && !target.includes("/")) {
    return "";
  }

  try {
    const url = new URL(target, window.location.origin);
    if (url.origin !== window.location.origin) return "";
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "";
  }
};

const getNotificationPath = (item) => {
  const data = item.data && typeof item.data === "object" ? item.data : {};
  const related =
    item.related && typeof item.related === "object" ? item.related : {};
  const action =
    item.action && typeof item.action === "object" ? item.action : {};
  const explicitPath = [
    item.redirectUrl,
    item.redirect_url,
    item.actionUrl,
    item.action_url,
    item.targetUrl,
    item.target_url,
    item.deepLink,
    item.deep_link,
    item.link,
    item.url,
    item.path,
    action.url,
    action.link,
    data.redirectUrl,
    data.redirect_url,
    data.actionUrl,
    data.action_url,
    data.link,
    data.url,
    data.path,
    related.redirectUrl,
    related.actionUrl,
    related.link,
    related.url,
  ]
    .map(getInternalNotificationPath)
    .find(Boolean);
  const typeValues = [
    item.notificationType,
    item.notification_type,
    item.type,
    data.notificationType,
    data.notification_type,
    data.type,
    related.notificationType,
    related.notification_type,
    related.type,
    item.category,
    item.eventType,
    item.event_type,
  ].filter((value) => value !== undefined && value !== null);
  const typeFields = typeValues.join(" ").toLowerCase();
  const contentText = `${item.title || ""} ${item.message || ""}`.toLowerCase();
  const numericTypeKind = typeValues
    .map((value) =>
      ({
        [NOTIFICATION_TYPES.CLIENT]: "client",
        [NOTIFICATION_TYPES.INQUIRY]: "inquiry",
        [NOTIFICATION_TYPES.CONTRACT]: "contract",
        [NOTIFICATION_TYPES.ORDER]: "order",
        [NOTIFICATION_TYPES.VENDOR]: "vendor",
      })[Number(value)],
    )
    .find(Boolean);
  const classifyType = (value) => {
    if (/quimera|booking/.test(value)) return "quimeraOrder";
    if (/request for (a )?quotation|\binquir|\brfq\b/.test(value))
      return "inquiry";
    if (/contract|quotation|\bquote\b|document/.test(value)) return "contract";
    if (/order|operation/.test(value)) return "order";
    if (/payment|transaction|invoice/.test(value)) return "payment";
    if (/vendor|profile/.test(value)) return "vendor";
    if (/client/.test(value)) return "client";
    return "";
  };
  const kind =
    numericTypeKind || classifyType(typeFields) || classifyType(contentText);
  const readId = (value) =>
    value && typeof value === "object"
      ? value.viewId ||
        value.view_id ||
        value.inquiryId ||
        value.orderId ||
        value.referenceId ||
        value.id ||
        value._id
      : value;
  const firstId = (...values) => values.map(readId).find(Boolean);
  const viewId = firstId(
    item.viewId,
    item.view_id,
    item.viewID,
    item.view,
    data.viewId,
    data.view_id,
    data.viewID,
    data.view,
    related.viewId,
    related.view_id,
    related.viewID,
    related.view,
    related.id,
    related._id,
  );
  const referenceId = firstId(
    item.referenceId,
    item.reference_id,
    item.refId,
    item.ref_id,
    item.relatedId,
    item.related_id,
    data.referenceId,
    data.reference_id,
    data.refId,
    data.ref_id,
    related.referenceId,
    related.reference_id,
    related.refId,
    related.ref_id,
  );
  const entityId = firstId(
    item.entityId,
    item.entity_id,
    data.entityId,
    data.entity_id,
    related.entityId,
    related.entity_id,
    viewId,
    referenceId,
  );
  const detailPath = (pattern, id) =>
    id ? pattern.replace(":id", encodeURIComponent(String(id))) : "";

  if (kind === "quimeraOrder") {
    return (
      detailPath(
        RoutesPaths.QUIMERA_ORDER_DETAILS,
        firstId(
          item.bookingId,
          item.quimeraOrderId,
          data.bookingId,
          data.quimeraOrderId,
          item.orderId,
          data.orderId,
          viewId,
          referenceId,
          entityId,
        ),
      ) ||
      RoutesPaths.QUIMERA_ORDERS
    );
  }
  if (kind === "inquiry") {
    return (
      detailPath(
        RoutesPaths.INQUIRIESDETAILS,
        firstId(
          item.inquiryId,
          item.inquiry_id,
          data.inquiryId,
          data.inquiry_id,
          related.inquiryId,
          related.inquiry_id,
          viewId,
          referenceId,
          entityId,
        ),
      ) || RoutesPaths.INQUIRIES
    );
  }
  if (kind === "contract") {
    const inquiryPath = detailPath(
      RoutesPaths.INQUIRIESDETAILS,
      firstId(
        item.inquiryId,
        item.inquiry_id,
        data.inquiryId,
        data.inquiry_id,
        related.inquiryId,
        related.inquiry_id,
        viewId,
        referenceId,
        entityId,
      ),
    );
    return inquiryPath ? `${inquiryPath}#quotation` : RoutesPaths.INQUIRIES;
  }
  if (kind === "order") {
    return (
      detailPath(
        RoutesPaths.ORDERDETAILS,
        firstId(
          item.orderId,
          item.order_id,
          data.orderId,
          data.order_id,
          related.orderId,
          related.order_id,
          viewId,
          referenceId,
          entityId,
        ),
      ) || RoutesPaths.ORDER
    );
  }
  if (kind === "payment") return explicitPath || RoutesPaths.TRANSACTIONS;
  if (kind === "vendor") {
    return explicitPath || RoutesPaths.COMPLETEPROFILE;
  }
  if (kind === "client") return explicitPath || RoutesPaths.INQUIRIES;
  return explicitPath || RoutesPaths.DASHBOARD;
};

export default function Header({ onToggleSidebar }) {
  const { language: selectedLanguage, t, text: tText } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authMe = useSelector((state) => state.auth?.authMe || {});
  const vendorProfileView = getVendorProfileView(authMe);
  const {
    items: notifications = [],
    unreadCount = 0,
    pagination: notificationPagination = {},
    loading: notificationsLoading = false,
    error: notificationsError = "",
  } = useSelector((state) => state.notification || {});

  const [menuVisible, setMenuVisible] = useState(false);
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);

  const [languageVisible, setLanguageVisible] = useState(false);

  const [logoutPopup, setLogoutPopup] = useState(false);

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationPage, setNotificationPage] = useState(1);

  const notificationRef = useRef(null);
  const languageRef = useRef(null);
  const profileRef = useRef(null);

  const languages = [
    {
      code: "en",
      name: t("language.english"),
      country: t("language.unitedStates"),
      flag: "/assets/usa-flag.png",
    },
    {
      code: "fr",
      name: t("language.french"),
      country: t("language.france"),
      flag: "/assets/france-flag.png",
    },
    {
      code: "pt-PT",
      name: t("language.portuguese"),
      country: t("language.portugal"),
      flag: "/assets/portugal-flag.svg",
    },
  ];

  const totalNotificationPages = Math.max(
    Number(notificationPagination.totalPages) || 1,
    1,
  );
  const currentNotificationPage =
    Number(notificationPagination.page) || notificationPage;
  const paginatedNotifications = notifications;
  const currentLanguage =
    languages.find((item) => item.code === selectedLanguage) || languages[0];

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language.code);
    setLanguageVisible(false);
  };

  const handleLogoutClick = () => {
    setMenuVisible(false);
    setLogoutPopup(true);
  };

  const handleLogoutCancel = () => {
    setLogoutPopup(false);
  };

  const handleLogoutConfirm = () => {
    dispatch(clearAuthSessionAction());

    setLogoutPopup(false);

    navigate("/", {
      replace: true,
    });
  };

  const handleNotificationToggle = () => {
    setNotificationOpen((prev) => !prev);

    setLanguageVisible(false);
    setMenuVisible(false);
  };

  const handlePreviousNotificationPage = () => {
    setNotificationPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextNotificationPage = () => {
    setNotificationPage((prev) => Math.min(prev + 1, totalNotificationPages));
  };

  useEffect(() => {
    dispatch(
      getNotificationListAction({
        page: notificationPage,
        limit: NOTIFICATIONS_PER_PAGE,
      }),
    );
  }, [dispatch, notificationPage]);

  useEffect(() => {
    dispatch(getUnreadNotificationCountAction());
  }, [dispatch]);
  /*
   * CLOSE NOTIFICATION WHEN CLICKING OUTSIDE
   */
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  /*
   * CLOSE LANGUAGE / PROFILE
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setMenuVisible(false);
      }

      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setLanguageVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /*
   * ESCAPE KEY
   */
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuVisible(false);
        setMobileSearchVisible(false);
        setLanguageVisible(false);
        setNotificationOpen(false);
        setLogoutPopup(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  /*
   * LOCK BODY ON LOGOUT POPUP
   */
  useEffect(() => {
    if (logoutPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [logoutPopup]);

  return (
    <>
      <header className="admin-header">
        {/* LEFT */}
        <div className="header-left">
          <button
            type="button"
            className="header-mobile-menu"
            onClick={onToggleSidebar}
            aria-label={t("ui.open.sidebar")}
          >
            <img src="/assets/menu-icon.svg" alt="" />
          </button>
        </div>

        {/* SEARCH */}
        {/* <div className="header-search">
          <img
            src="/assets/search-icon.svg"
            alt=""
            className="header-search-icon"
          />

          <input
            type="search"
            placeholder="Search for anything..."
            aria-label="Search"
          />
        </div> */}

        {/* MOBILE SEARCH */}
        <button
          type="button"
          className="mobile-search-btn"
          onClick={() => setMobileSearchVisible((prev) => !prev)}
          aria-label={t("ui.search.14w27d4")}
        >
          <img src="/assets/search-icon.svg" alt="" />
        </button>

        {/* RIGHT */}
        <div className="header-right">
          {/* LANGUAGE */}
          <div className="language-wrapper" ref={languageRef}>
            <button
              type="button"
              className="header-circle-btn language-btn"
              aria-label={t("ui.select.language")}
              aria-expanded={languageVisible}
              onClick={() => {
                setLanguageVisible((prev) => !prev);

                setNotificationOpen(false);
                setMenuVisible(false);
              }}
            >
              <span className="language-flag">
                <img src={currentLanguage.flag} alt={currentLanguage.name} />
              </span>
            </button>

            {languageVisible && (
              <div className="language-dropdown">
                {languages.map((language) => (
                  <button
                    type="button"
                    key={language.code}
                    className={`language-option ${
                      selectedLanguage === language.code ? "active" : ""
                    }`}
                    onClick={() => handleLanguageChange(language)}
                  >
                    <span className="language-option-flag">
                      <img src={language.flag} alt="" />
                    </span>

                    <span className="language-option-content">
                      <strong>{language.name}</strong>

                      <small>{language.country}</small>
                    </span>

                    {selectedLanguage === language.code && (
                      <span className="language-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div
            className="header-notification-wrapper me-2"
            ref={notificationRef}
          >
            <button
              type="button"
              className="header-circle-btn notification-btn"
              aria-label={t("ui.notifications")}
              aria-expanded={notificationOpen}
              onClick={handleNotificationToggle}
            >
              <img src="/assets/notification.svg" alt="" />

              {unreadCount > 0 && (
                <span className="notification-count">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {notificationOpen && (
              <div className="notification-dropdown">
                {/* HEADER */}
                <div className="notification-dropdown-header">
                  <div>
                    <h3>{t("ui.notifications")}</h3>

                    <p>
                      {unreadCount > 0
                        ? t(
                            unreadCount === 1
                              ? "notificationMessages.unreadOne"
                              : "notificationMessages.unreadMany",
                            { count: unreadCount },
                          )
                        : t("ui.notificationsCaughtUp")}
                    </p>
                  </div>

                </div>

                {/* LIST */}
                <div className="notification-dropdown-list">
                  {notificationsLoading ? (
                    <div className="notification-empty">
                      <LoadingSpinner className="notification-loading-state" label={t("ui.loadingNotifications")} />
                    </div>
                  ) : notificationsError ? (
                    <div className="notification-empty" role="alert"><strong>{t("ui.unable.to.load.notifications")}</strong><span>{tText(notificationsError)}</span></div>
                  ) : paginatedNotifications.length > 0 ? (
                    paginatedNotifications.map((item) => (
                      <button
                        type="button"
                        key={item.id}
                        className={`notification-dropdown-item ${
                          !item.read ? "notification-unread" : ""
                        }`}
                        onClick={() => {
                          if (!item.read) dispatch(markNotificationReadAction(item.id));
                          setNotificationOpen(false);
                          navigate(getNotificationPath(item));
                        }}
                      >
                        <div className="notification-item-icon">
                          <img src="/assets/notification.svg" alt="" />
                        </div>

                        <div className="notification-item-content">
                          <div className="notification-item-title-row">
                            <strong>{item.title}</strong>

                            {!item.read && (
                              <span className="notification-unread-dot" />
                            )}
                          </div>

                          <p>{item.message}</p>

                          {/* <small>
                              {item.time}
                            </small> */}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="notification-empty">
                      <img src="/assets/notification.svg" alt="" />

                      <strong>{t("ui.no.notifications")}</strong>

                      <span>{t("ui.you.re.all.caught.up")}</span>
                    </div>
                  )}
                </div>

                {/* PAGINATION */}
                {totalNotificationPages > 1 && (
                  <div className="notification-dropdown-footer">
                    <div className="notification-pagination">
                      {/* PREVIOUS */}
                      <button
                        type="button"
                        className="notification-pagination-arrow"
                        disabled={currentNotificationPage <= 1 || notificationsLoading}
                        onClick={handlePreviousNotificationPage}
                        aria-label={t("ui.previous.notifications")}
                      >
                        ‹
                      </button>

                      {/* PAGE NUMBERS */}
                      {Array.from(
                        {
                          length: totalNotificationPages,
                        },
                        (_, index) => index + 1,
                      ).map((page) => (
                        <button
                          type="button"
                          key={page}
                          className={
                            currentNotificationPage === page
                              ? "notification-pagination-page active"
                              : "notification-pagination-page"
                          }
                          onClick={() => setNotificationPage(page)} disabled={notificationsLoading}
                        >
                          {page}
                        </button>
                      ))}

                      {/* NEXT */}
                      <button
                        type="button"
                        className="notification-pagination-arrow"
                        disabled={currentNotificationPage >= totalNotificationPages || notificationsLoading}
                        onClick={handleNextNotificationPage}
                        aria-label={t("ui.next.notifications")}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="header-user-wrapper" ref={profileRef}>
            <button
              type="button"
              className="header-user"
              onClick={() => {
                setMenuVisible((prev) => !prev);

                setNotificationOpen(false);
                setLanguageVisible(false);
              }}
              aria-expanded={menuVisible}
            >
              <div className="user-avatar">
                <span>{vendorProfileView.initial}</span>
                {vendorProfileView.imageUrl && (
                  <img
                    src={vendorProfileView.imageUrl}
                    alt={vendorProfileView.name ? `${vendorProfileView.name} ${t("ui.profile")}` : t("ui.profile")}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                )}
              </div>
            </button>

            {menuVisible && (
              <div className="profile-dropdown">
                <button
                  type="button"
                  onClick={() => {
                    setMenuVisible(false);

                    navigate("/setting?tab=profile");
                  }}
                >
                  <img src="/assets/user.svg" alt="" />

                  <span>{t("ui.my.profile.xc1c2d")}</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMenuVisible(false);

                    navigate("/setting?tab=settings");
                  }}
                >
                  <img src="/assets/setting.svg" alt="" />

                  <span>{t("ui.settings")}</span>
                </button>

                <div className="profile-dropdown-line" />

                <button
                  type="button"
                  className="logout-btn"
                  onClick={handleLogoutClick}
                >
                  <img src="/assets/logout.svg" alt="" />

                  <span>{t("ui.logout")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ===================================
          MOBILE SEARCH
      =================================== */}

      {mobileSearchVisible && (
        <div className="mobile-search-area">
          <div className="mobile-search-box">
            <img src="/assets/search-icon.svg" alt="" />

            <input
              type="search"
              placeholder={t("ui.search.for.anything")}
              autoFocus
            />

            <button
              type="button"
              onClick={() => setMobileSearchVisible(false)}
              aria-label={t("ui.close.search")}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* ===================================
          LOGOUT POPUP
      =================================== */}

      {logoutPopup && (
        <div
          className="logout-modal-overlay"
          onClick={handleLogoutCancel}
          role="presentation"
        >
          <div
            className="logout-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-title"
          >
            <div className="logout-modal-icon">
              <img src="/assets/logout.svg" alt="" />
            </div>

            <h3 id="logout-title">{t("ui.logout.x70xcl")}</h3>

            <p>{t("ui.are.you.sure.you.want.to.logout.from.your.account")}</p>

            <div className="logout-modal-actions">
              <button
                type="button"
                className="logout-cancel-btn"
                onClick={handleLogoutCancel}
              >{t("ui.cancel")}</button>

              <button
                type="button"
                className="logout-confirm-btn"
                onClick={handleLogoutConfirm}
              >{t("ui.logout.a799ze")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
